import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { isDevMode } from '@angular/core';
import { throwError } from 'rxjs';

interface SimulatedError {
  status: number;
  statusText: string;
  /** how many upcoming requests should fail */
  remaining: number;
  /** only requests whose url contains this string fail (all requests if omitted) */
  urlMatch?: string;
}

let simulated: SimulatedError | null = null;

const STATUS_TEXT: Record<number, string> = {
  0: 'Unknown Error',
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout',
};

/**
 * Lets the next HTTP request(s) fail with the given status (default 503) without contacting the server.
 * Only works in dev mode.
 *
 * In the browser console:
 *   dmbSimulateHttpError()                 // next request fails with 503
 *   dmbSimulateHttpError(503, 3)           // next 3 requests fail
 *   dmbSimulateHttpError(503, 1, '/users') // next request containing '/users' fails
 *   dmbSimulateHttpError(0)                // simulate a network error
 *   dmbClearSimulatedHttpError()
 */
export function simulateHttpError(status = 503, count = 1, urlMatch?: string): void {
  if (!isDevMode()) {
    console.warn('simulateHttpError is only available in dev mode');
    return;
  }
  simulated = { status, statusText: STATUS_TEXT[status] ?? 'Simulated Error', remaining: count, urlMatch };
  console.info(
    `[dmb] next ${count} request(s)${urlMatch ? ` matching "${urlMatch}"` : ''} will fail with HTTP ${status}`,
  );
}

export function clearSimulatedHttpError(): void {
  simulated = null;
}

/**
 * Must be registered AFTER httpErrorInterceptor, so the error page interceptor sees the simulated error:
 *   withInterceptors([httpErrorInterceptor, httpErrorSimulationInterceptor])
 */
export const httpErrorSimulationInterceptor: HttpInterceptorFn = (req, next) => {
  if (!simulated || !isDevMode() || (simulated.urlMatch && !req.urlWithParams.includes(simulated.urlMatch))) {
    return next(req);
  }

  const { status, statusText } = simulated;
  simulated.remaining--;
  if (simulated.remaining <= 0) {
    simulated = null;
  }

  return throwError(
    () =>
      new HttpErrorResponse({
        status,
        statusText,
        url: req.urlWithParams,
        error: { message: `Simulated HTTP ${status}` },
      }),
  );
};

if (isDevMode() && typeof window !== 'undefined') {
  Object.assign(window, {
    dmbSimulateHttpError: simulateHttpError,
    dmbClearSimulatedHttpError: clearSimulatedHttpError,
  });
}
